/**
 * Typed API Client for Car-IA Backend
 * Matches API-CONTRACT.md v1.3
 */

import type {
  UUID,
  Angle,
  CreateInspectionRequest,
  CreateInspectionSuccessResponse,
  BatchStatusResponse,
  InspectionReport,
  ReprocessResponse,
  InspectionMetadata,
  ValidationErrorDetail,
} from '../types/api';

import { REQUIRED_ANGLES } from '../types/api';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1';

// ============================================================
// Error Classes
// ============================================================

export class ApiError extends Error {
  public readonly status: number;
  public readonly code: string;
  public readonly details?: ValidationErrorDetail[];
  public readonly inspectionId?: UUID;

  constructor(
    message: string,
    status: number,
    code: string,
    details?: ValidationErrorDetail[],
    inspectionId?: UUID
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.inspectionId = inspectionId;
  }

  static fromResponse(response: Response, data: unknown): ApiError {
    const errorData = data as Record<string, unknown>;
    const status = response.status;
    const code = (errorData.error as string) || 'unknown_error';
    const message = (errorData.message as string) || ApiError.defaultMessage(code);
    const details = errorData.details as ValidationErrorDetail[] | undefined;
    const inspectionId = errorData.inspection_id as UUID | undefined;

    return new ApiError(message, status, code, details, inspectionId);
  }

  private static defaultMessage(code: string): string {
    const messages: Record<string, string> = {
      validation_error: 'Verifique os dados e tente novamente',
      duplicate: 'Esta vistoria já foi processada',
      payload_too_large: 'Fotos muito grandes. Recapture com mais luz.',
      precheck_failed: 'Alguma foto não passou na verificação de qualidade',
      not_found: 'Vistoria não encontrada',
      expired: 'Esta vistoria expirou',
      unauthorized: 'Acesso não autorizado',
      server_error: 'Erro interno do servidor. Tente novamente.',
      unknown_error: 'Ocorreu um erro inesperado',
    };
    return messages[code] || messages.unknown_error;
  }
}

export class NetworkError extends Error {
  constructor(message = 'Erro de conexão. Verifique sua internet.') {
    super(message);
    this.name = 'NetworkError';
  }
}

// ============================================================
// HTTP Client
// ============================================================

interface RequestOptions extends RequestInit {
  apiKey?: string;
  idempotencyKey?: string;
  timeout?: number;
}

async function fetchWithTimeout(
  url: string,
  options: RequestOptions = {}
): Promise<Response> {
  const { timeout = 30000, apiKey, idempotencyKey, ...fetchOptions } = options;

  const headers = new Headers(fetchOptions.headers);
  if (apiKey) {
    headers.set('X-Api-Key', apiKey);
  }
  if (idempotencyKey) {
    headers.set('Idempotency-Key', idempotencyKey);
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      headers,
      signal: controller.signal,
    });
    return response;
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new NetworkError('Tempo de conexão esgotado');
    }
    throw new NetworkError();
  } finally {
    clearTimeout(timeoutId);
  }
}

// ============================================================
// API Methods
// ============================================================

/**
 * Generate a UUID v4 for idempotency key
 */
export function generateIdempotencyKey(): UUID {
  return crypto.randomUUID();
}

/**
 * POST /inspections - Create inspection with multipart upload
 */
export async function createInspection(
  request: CreateInspectionRequest,
  options: { apiKey?: string; idempotencyKey: UUID; onProgress?: (progress: number) => void } = { idempotencyKey: generateIdempotencyKey() }
): Promise<CreateInspectionSuccessResponse> {
  const formData = new FormData();
  
  formData.append('plate', request.plate);
  formData.append('metadata', JSON.stringify(request.metadata));

  // Append photos in required order
  for (const angle of REQUIRED_ANGLES) {
    const file = request.photos[angle];
    if (file) {
      formData.append(`photos[${angle}]`, file, `${angle}.jpg`);
    }
  }

  // Vídeo motor→escape (opcional, contrato v1.3 §1.1)
  if (request.video) {
    formData.append('video[engine_sweep]', request.video, 'engine_sweep.webm');
  }

  // Upload com vídeo pode ser grande — timeout estendido (padrão 30s → 120s)
  const response = await fetchWithTimeout(`${API_BASE_URL}/inspections`, {
    method: 'POST',
    body: formData,
    apiKey: options.apiKey,
    idempotencyKey: options.idempotencyKey,
    timeout: 120000,
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const error = ApiError.fromResponse(response, data);
    
    // Special handling for 409 - return the existing inspection ID
    if (response.status === 409 && error.inspectionId) {
      throw new ApiError(
        'Vistoria já processada',
        409,
        'duplicate',
        undefined,
        error.inspectionId
      );
    }
    
    throw error;
  }

  return data as CreateInspectionSuccessResponse;
}

/**
 * GET /inspections/status - Batch status polling
 */
export async function getBatchStatus(
  ids: UUID[],
  apiKey?: string
): Promise<BatchStatusResponse> {
  if (ids.length === 0) return [];
  if (ids.length > 50) {
    throw new ApiError('Máximo de 50 IDs por requisição', 400, 'validation_error');
  }

  const params = new URLSearchParams({ ids: ids.join(',') });
  const response = await fetchWithTimeout(`${API_BASE_URL}/inspections/status?${params}`, {
    method: 'GET',
    apiKey,
  });

  const data = await response.json().catch(() => []);

  if (!response.ok) {
    throw ApiError.fromResponse(response, data);
  }

  return data as BatchStatusResponse;
}

/**
 * Resolve um caminho de foto/vídeo do laudo (API-relativo, contrato v1.3 §1.3)
 * para uma URL completa. O endpoint exige X-Api-Key — para carregar em
 * <img>/<video>, use fetchPhotoBlobUrl().
 */
export function resolvePhotoUrl(path: string): string {
  if (/^(https?:|data:|blob:)/.test(path)) return path;
  return `${API_BASE_URL}${path}`;
}

/**
 * Busca uma foto/vídeo do laudo com autenticação (X-Api-Key) e devolve um
 * object URL pronto para <img>/<video>. Revogue com URL.revokeObjectURL()
 * quando não precisar mais.
 */
export async function fetchPhotoBlobUrl(path: string, apiKey?: string): Promise<string> {
  const response = await fetchWithTimeout(resolvePhotoUrl(path), {
    method: 'GET',
    apiKey,
    timeout: 15000,
  });

  if (!response.ok) {
    throw new ApiError('Falha ao carregar mídia do laudo', response.status, 'image_load_error');
  }

  const blob = await response.blob();
  return URL.createObjectURL(blob);
}

/**
 * GET /inspections/{id}/report - Get full report
 */
export async function getInspectionReport(
  inspectionId: UUID,
  apiKey?: string
): Promise<InspectionReport> {
  const response = await fetchWithTimeout(`${API_BASE_URL}/inspections/${inspectionId}/report`, {
    method: 'GET',
    apiKey,
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw ApiError.fromResponse(response, data);
  }

  return data as InspectionReport;
}

/**
 * POST /inspections/{id}/reprocess - Reprocess inspection
 */
export async function reprocessInspection(
  inspectionId: UUID,
  options: { apiKey?: string; idempotencyKey: UUID } = { idempotencyKey: generateIdempotencyKey() }
): Promise<ReprocessResponse> {
  const response = await fetchWithTimeout(`${API_BASE_URL}/inspections/${inspectionId}/reprocess`, {
    method: 'POST',
    apiKey: options.apiKey,
    idempotencyKey: options.idempotencyKey,
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw ApiError.fromResponse(response, data);
  }

  return data as ReprocessResponse;
}

/**
 * Health check
 */
export async function healthCheck(): Promise<{ status: string }> {
  const response = await fetchWithTimeout(`${API_BASE_URL.replace('/v1', '')}/health`, {
    method: 'GET',
  });
  return response.json();
}

// ============================================================
// Utility Functions
// ============================================================

/**
 * Validate Brazilian plate format (Mercosul: AAA0A00 or old: AAA-0000)
 */
export function validatePlate(plate: string): boolean {
  const mercosulRegex = /^[A-Z]{3}[0-9][A-Z][0-9]{2}$/i;
  const oldRegex = /^[A-Z]{3}-?[0-9]{4}$/i;
  return mercosulRegex.test(plate) || oldRegex.test(plate);
}

/**
 * Format plate for display (AAA-0000 or AAA0A00)
 */
export function formatPlate(plate: string): string {
  const clean = plate.toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (clean.length === 7) {
    // Check if Mercosul format (letter at position 4)
    if (/^[A-Z]{3}[0-9][A-Z][0-9]{2}$/.test(clean)) {
      return `${clean.slice(0, 3)}${clean.slice(3, 4)}${clean.slice(4)}`; // ABC1D23
    }
    // Old format
    return `${clean.slice(0, 3)}-${clean.slice(3)}`; // ABC-1234
  }
  return plate.toUpperCase();
}

/**
 * Generate inspection metadata
 */
export function createInspectionMetadata(
  deviceId: string,
  appVersion: string,
  additional?: Partial<InspectionMetadata>
): InspectionMetadata {
  return {
    angles: REQUIRED_ANGLES,
    device_id: deviceId,
    app_version: appVersion,
    timestamp: new Date().toISOString(),
    user_agent: navigator.userAgent,
    ...additional,
  };
}

/**
 * Get angle display label in Portuguese
 */
export function getAngleLabel(angle: Angle): string {
  const labels = {
    front: 'Frontal',
    rear: 'Traseira',
    side_right: 'Lateral Dir.',
    side_left: 'Lateral Esq.',
    dashboard: 'Painel',
    engine: 'Motor',
    trunk: 'Porta-malas',
    chassis: 'Chassi',
  } as const;
  return labels[angle];
}

/**
 * Get angle icon
 */
export function getAngleIcon(angle: Angle): string {
  const icons = {
    front: 'directions_car',
    rear: 'directions_car',
    side_right: 'directions_car',
    side_left: 'directions_car',
    dashboard: 'dashboard',
    engine: 'settings',
    trunk: 'luggage',
    chassis: 'architecture',
  } as const;
  return icons[angle];
}

/**
 * Get angle capture instructions
 */
export function getAngleInstructions(angle: Angle): string {
  const instructions = {
    front: 'Centralize a frente do veículo, inclua faróis e placa',
    rear: 'Capture a traseira completa com lanternas e placa',
    side_right: 'Fotografe a lateral direita inteira, rodas ao chão',
    side_left: 'Fotografe a lateral esquerda inteira, rodas ao chão',
    dashboard: 'Painel ligado, mostre hodômetro e indicadores',
    engine: 'Capô aberto, motor limpo, evite reflexos',
    trunk: 'Porta-malas aberto, mostre estepe e forro',
    chassis: 'Número do chassi legível (parabrisa ou porta)',
  } as const;
  return instructions[angle];
}